<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search users..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchUsers"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Status</label>
          <select v-model="filters.active_status">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </template>
      <template #actions>
        <router-link
          to="/users/invites"
          class="btn-primary"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
          Invites
        </router-link>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || users.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">User</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Is Admin</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Status</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Last Login</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 6" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-gray-100 skeleton-pulse shrink-0" />
                <div class="space-y-2 flex-1">
                  <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" />
                  <div class="h-3 bg-gray-50 rounded skeleton-pulse w-16" />
                </div>
              </div>
            </td>
            <td class="px-6 py-4"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-8 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="item in users" :key="item._id" class="hover:bg-gray-50/50 transition-colors">
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-[var(--color-primary-light)] text-[var(--color-primary)] flex items-center justify-center text-sm font-semibold shrink-0">
                  {{ getInitials(item) }}
                </div>
                <div class="min-w-0">
                  <p class="text-[14px] text-gray-900 truncate">{{ item.first_name }} {{ item.last_name }}</p>
                  <p class="text-sm text-gray-500 truncate">{{ item.email }}</p>
                </div>
              </div>
            </td>
            <td class="px-6 py-4">
              <span
                :class="[
                  'inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold',
                  item.is_admin
                    ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                    : 'bg-gray-100 text-gray-400'
                ]"
              >
                {{ item.is_admin ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm" :class="item.is_active ? 'badge-live' : 'badge-idle'">
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ $filters.formatDatetime(item.last_login?.date) }}</span>
            </td>
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="userActions(item)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && (search || filters.active_status)" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching users found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
        </svg>
      </div>
      <p class="text-base font-medium text-gray-900">No users yet</p>
      <p class="text-sm text-gray-500 mt-1">Invite team members to get started</p>
    </div>

  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'

export default {
  name: 'UsersList',

  components: { ListToolbar, ActionsMenu },

  data() {
    return {
      loading: true,
      users: [],
      search: '',
      filters: { active_status: 'active' },
      sort: 'name:1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'updated:1', label: 'Last Updated (oldest)' },
        { value: 'updated:-1', label: 'Last Updated (newest)' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' },
        { value: 'login:1', label: 'Last Login (oldest)' },
        { value: 'login:-1', label: 'Last Login (newest)' }
      ],
      pagination: { limit: 50, page: 1, total: 0 }
    }
  },

  computed: {
    activeFilterCount() {
      return Object.values(this.filters).filter(v => v !== '').length
    }
  },

  watch: {
    search() {
      this.pagination.page = 1
      this.fetchUsers()
    },
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1
        this.fetchUsers()
      }
    },
    sort() {
      this.pagination.page = 1
      this.fetchUsers()
    }
  },

  mounted() {
    this.fetchUsers()
  },

  methods: {
    clearFilters() {
      this.filters = { active_status: '' }
    },

    async fetchUsers(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.filters.active_status) params.status = this.filters.active_status
        if (this.sort) params.sort = this.sort
        if (count) params.count = true

        const res = await api.get('/users', { params })
        this.users = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch {
        // keep existing data
      } finally {
        this.loading = false
      }
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchUsers(false)
    },

    userActions(user) {
      return [
        { label: 'Edit', action: () => this.editUser(user), icon: icons.edit }
      ]
    },

    editUser(user) {
      this.$router.push(`/users/${user._id}/edit`)
    },

    getInitials(user) {
      const f = user.first_name?.[0] || ''
      const l = user.last_name?.[0] || ''
      return (f + l).toUpperCase() || '?'
    },

  }
}
</script>
